import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const POLLING_INTERVAL = 10000;
const API_BASE_URL_CONST = process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

const StatusBadge = ({ isRead }) => {
  return (
    <span style={{ fontWeight: 600, color: isRead ? '#16A34A' : '#CA8A04' }}>
      {isRead ? 'Read' : 'Unread'}
    </span>
  );
};

function NotificationsListPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latestNotificationsRef = useRef(notifications);

  useEffect(() => {
    latestNotificationsRef.current = notifications;
  }, [notifications]);

  const fetchNotifications = useCallback(async (isPoll = false) => {
    if (!isPoll) setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL_CONST}/notifications/notifications.php`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const newNotifications = result.data.filter(
          (notification) => !latestNotificationsRef.current.some((n) => n.id === notification.id)
        );

        if (newNotifications.length > 0) {
          newNotifications.forEach((notification) => {
            toast.info(`New notification: ${notification.title || "Untitled Notification"}`);
          });
        }

        setNotifications(result.data);
        setError(null);
      } else {
        throw new Error(result.error || "Unexpected API response");
      }
    } catch (err) {
      if (!isPoll) setError(err.message);
    } finally {
      if (!isPoll) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(false);

    const interval = setInterval(() => {
      if (!document.hidden) fetchNotifications(true);
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  if (loading) return <p style={{ color: "#fff", textAlign: "center", padding: "20px" }}>Loading notifications...</p>;

  if (error) {
    return (
      <div
        style={{
          padding: "20px",
          margin: "20px auto",
          background: "rgba(220, 38, 38, 0.15)",
          borderRadius: "10px",
          maxWidth: "800px",
          textAlign: "center",
          color: "#fff",
        }}
      >
        <h3 style={{ color: "#DC2626" }}>Error Fetching Notifications</h3>
        <p>{error}</p>
        <button
          onClick={() => fetchNotifications(false)}
          style={{
            padding: "10px 20px",
            marginTop: "10px",
            background: "#1E3A8A",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
      }}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "1200px",
          margin: "0 auto",
          boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
          backdropFilter: "none",
        }}
      >
        <h2 style={{ textAlign: "center", fontSize: "28px", fontWeight: "600", marginBottom: "25px" }}>
          Notifications
        </h2>

        {notifications.length === 0 ? (
          <p style={{ textAlign: "center" }}>No notifications found.</p>
        ) : (
          <div style={{ overflowX: "auto", willChange: "transform" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "rgba(255, 255, 255, 0.9)",
                borderRadius: "10px",
                overflow: "hidden",
                color: "#1E293B",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr style={{ background: "#1E3A8A", color: "#fff" }}>
                  <th style={{ padding: "14px", textAlign: "left" }}>ID</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Title</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Message</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Date</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification, index) => (
                  <tr
                    key={notification.id || index}
                    style={{
                      background: index % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                      borderBottom: "1px solid #E5E7EB",
                      transition: "background 0.2s ease",
                      fontWeight: !notification.is_read ? 600 : 400,
                    }}
                  >
                    <td style={{ padding: "12px" }}>{notification.id}</td>
                    <td style={{ padding: "12px" }}>{notification.title || "N/A"}</td>
                    <td style={{ padding: "12px" }}>{notification.message || "N/A"}</td>
                    <td style={{ padding: "12px" }}>
                      {notification.created_at ? new Date(notification.created_at).toLocaleString() : "N/A"}
                    </td>
                    <td style={{ padding: "12px" }}>
                      <StatusBadge isRead={notification.is_read} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationsListPage;
